import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api';
import { BlockRenderer, formatInline, Md, type BlockPayload } from '../components/BlockRenderer';
import { BooksReaderModuleShell } from '../components/books/BooksReaderModuleShell';
import { Shell } from '../components/Shell';
import './ModulePage.css';
import '../wi-module-premium.css';

type SectionRow = {
  id: string;
  position: number;
  layer: string;
  title: string | null;
  body_md: string | null;
};

type LegacyPerspective = {
  id: string;
  perspective_key: string;
  position: number;
  title: string | null;
  body_md: string | null;
  default_visibility?: string | null;
};

type MicroModuleRow = {
  id: string;
  position: number;
  title: string | null;
  estimated_minutes: number;
  block_id: string | null;
};

type ModulePayload = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  estimated_read_minutes?: number | null;
  topic_family?: string | null;
  difficulty?: string | null;
  sensitivity?: number;
  review_state?: string | null;
  framing: null | {
    issue?: string;
    why_asked?: string;
    why_matters?: string;
    misunderstandings?: string[];
  };
  blocks: BlockPayload[];
  sections: SectionRow[];
  perspectives: LegacyPerspective[];
  relationships: Array<{ kind: string; type?: string; slug: string; title: string; id: string }>;
  microModules?: MicroModuleRow[];
  journeysContaining?: Array<{ slug: string; title: string; step_position: number }>;
  topics?: Array<{
    slug: string;
    title: string;
    family?: string | null;
    summary?: string | null;
    weight?: number;
    primary_link?: number;
  }>;
  comparePages?: Array<{
    slug: string;
    title: string;
    summary?: string | null;
    role?: string;
    position?: number;
  }>;
};

type ReaderState = {
  bookmarked: boolean;
  notePreview: string | null;
  lastInteractionKind: string | null;
  helpfulFlag?: boolean;
  confusingFlag?: boolean;
  completionPercent?: number;
  progressState?: string | null;
  lastSeenAt?: number | null;
};

const depthModes = [
  { id: 'simple' as const, label: 'Short answer' },
  { id: 'full' as const, label: 'Full explanation' },
  { id: 'study' as const, label: 'Deeper analysis' },
];

type DepthId = (typeof depthModes)[number]['id'];

function MdSection({ body }: { body: string }) {
  return (
    <div className="wi-module-section-md">
      <Md>{body}</Md>
    </div>
  );
}

function blockNavLabel(b: BlockPayload, index: number): string {
  const p = b.payload;
  const titled = typeof p.title === 'string' ? p.title.trim() : '';
  if (titled) return titled;
  if (b.blockType === 'faq_item' && typeof p.question === 'string') {
    const q = p.question.trim();
    return q.length > 56 ? `${q.slice(0, 56)}...` : q;
  }
  if (b.blockType === 'quote_block') {
    if (typeof p.attribution === 'string' && p.attribution.trim()) {
      return `Quote - ${p.attribution.trim()}`;
    }
    return 'Featured quote';
  }
  if (b.blockType === 'glossary_term' && typeof p.term === 'string') return p.term.trim();
  return `Section ${index + 1}`;
}


function humanizeTopicFamily(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.replace(/_/g, ' ').trim();
  if (!s) return null;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function humanizeDifficulty(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const map: Record<string, string> = {
    intro: 'Introductory',
    core: 'Core',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  };
  return map[raw.toLowerCase()] ?? raw.charAt(0).toUpperCase() + raw.slice(1);
}

function layerVisible(layer: string, mode: DepthId): boolean {
  const l = (layer || '').toLowerCase();
  if (l === 'concise' || l === 'simple' || l === 'short') return true;
  if (l === 'deeper' || l === 'study' || l === 'advanced') return mode === 'study';
  if (l === 'full' || l === 'main') return mode !== 'simple';
  return mode !== 'simple';
}

export function ModulePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const journeySlug = searchParams.get('journey');
  const from = searchParams.get('from');
  const parentSlug = searchParams.get('parent');

  const [mod, setMod] = useState<ModulePayload | null>(null);
  const [readerState, setReaderState] = useState<ReaderState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<DepthId>('full');
  const [audienceTag, setAudienceTag] = useState<string | null>(null);
  // Default should be core-only until we know the reader's lens.
  const [activePerspective, setActivePerspective] = useState<string>('none');
  const [profileLens, setProfileLens] = useState<string | null>(null);
  const [hasOnboarding, setHasOnboarding] = useState(false);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);
  const [bookmarkAck, setBookmarkAck] = useState<string | null>(null);
  const [bookmarkErr, setBookmarkErr] = useState<string | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [noteMicroId, setNoteMicroId] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteAck, setNoteAck] = useState<string | null>(null);
  const [noteErr, setNoteErr] = useState<string | null>(null);
  const [journeyCtx, setJourneyCtx] = useState<{ title: string; slug: string; step: number; total: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [feedbackAck, setFeedbackAck] = useState<string | null>(null);

  useEffect(() => {
    setFeedbackStatus('idle');
    setFeedbackAck(null);
    setBookmarkAck(null);
    setBookmarkErr(null);
    setNoteAck(null);
    setNoteErr(null);
  }, [slug]);

  useEffect(() => {
    try {
      setHasOnboarding(localStorage.getItem('wi_onboarding_done') === '1');
    } catch {
      setHasOnboarding(false);
    }
  }, []);

  useEffect(() => {
    try {
      const d = localStorage.getItem('wi_depth_pref') as DepthId | null;
      if (d === 'simple' || d === 'full' || d === 'study') {
        setMode(d);
        return;
      }
    } catch {
      /* ignore */
    }
    let cancelled = false;
    const applyExplicit = (explicit?: {
      preferredModuleDepth?: string;
      worldviewBackground?: string;
      motive?: string;
      familiarityWithIslam?: string;
      reasoningPreference?: string;
    }) => {
      const p = explicit?.preferredModuleDepth;
      const worldview = explicit?.worldviewBackground;
      const motive = explicit?.motive;
      const familiarity = explicit?.familiarityWithIslam;
      const reasoning = explicit?.reasoningPreference;
      if (!cancelled && (p === 'simple' || p === 'full' || p === 'study')) setMode(p);
      if (!cancelled) {
        const w = typeof worldview === 'string' ? worldview.trim().toLowerCase() : '';
        if (w) setAudienceTag(w);

        let lens: string | null = null;
        if (familiarity === 'totally_new') lens = 'beginner';
        else if (w === 'muslim' && motive === 'doubt') lens = 'muslim_doubts';
        else if (w) lens = w;
        else if (reasoning === 'philosophical') lens = 'philosophical';
        else if (reasoning === 'historical') lens = 'historical';
        else if (reasoning === 'comparative') lens = 'comparative';
        else if (reasoning === 'researcher') lens = 'researcher';

        if (lens === 'spiritual') lens = 'spiritual_not_religious';
        if (lens) setProfileLens(lens);
      }
    };

    (async () => {
      try {
        const r = await apiJson<{
          profile: {
            explicit: {
              preferredModuleDepth?: string;
              worldviewBackground?: string;
              motive?: string;
              familiarityWithIslam?: string;
              reasoningPreference?: string;
            };
          };
        }>('/api/me/profile');
        applyExplicit(r.profile?.explicit);
      } catch {
        // Fallback: if onboarding was completed on this device, use the locally cached explicit answers.
        try {
          const raw = localStorage.getItem('wi_profile_explicit_v1');
          if (raw) applyExplicit(JSON.parse(raw));
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<{ module: ModulePayload; readerState: ReaderState }>(`/api/modules/${slug}`);
        if (!cancelled) {
          setMod(res.module);
          setReaderState(res.readerState);
          if (res.readerState.notePreview) setNoteBody(res.readerState.notePreview);
          setNoteMicroId('');
        }
        try {
          const raw = JSON.stringify({
            slug: res.module.slug,
            title: res.module.title,
            at: Date.now(),
          });
          localStorage.setItem('wi_explore_last', raw);
          localStorage.setItem('wi_study_last', raw);
        } catch {
          /* ignore */
        }
        await apiJson('/api/interactions', {
          method: 'POST',
          body: JSON.stringify({ moduleId: res.module.id, kind: 'started' }),
        });
        await apiJson('/api/events', {
          method: 'POST',
          body: JSON.stringify({
            eventType: 'module_viewed',
            entityType: 'module',
            entityId: res.module.id,
          }),
        });

        if (journeySlug) {
          try {
            const jr = await apiJson<{
              journey: { title: string; slug: string };
              steps: Array<{ position: number; module_slug: string }>;
            }>(`/api/journeys/${journeySlug}`);
            const idx = jr.steps.findIndex((s) => s.module_slug === res.module.slug);
            setJourneyCtx({
              title: jr.journey.title,
              slug: jr.journey.slug,
              step: idx >= 0 ? idx + 1 : 1,
              total: jr.steps.length,
            });
            await apiJson(`/api/journeys/${journeySlug}/state`, {
              method: 'POST',
              body: JSON.stringify({
                lastStepPosition: idx >= 0 ? idx + 1 : 1,
              }),
            });
          } catch {
            setJourneyCtx(null);
          }
        } else {
          setJourneyCtx(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, journeySlug]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const doc = document.documentElement;
      const h = doc.scrollHeight - doc.clientHeight;
      setScrollPct(h > 0 ? Math.min(100, Math.round((doc.scrollTop / h) * 100)) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [mod]);

  useEffect(() => {
    if (scrollPct < 72 || !mod) return;
    const t = window.setTimeout(() => {
      apiJson('/api/interactions', {
        method: 'POST',
        body: JSON.stringify({
          moduleId: mod.id,
          kind: 'progress',
          payload: { scrollApprox: scrollPct },
        }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [scrollPct, mod]);

  const setDepth = (d: DepthId) => {
    setMode(d);
    try {
      localStorage.setItem('wi_depth_pref', d);
    } catch {
      /* ignore */
    }
  };

  const saveBookmark = async () => {
    if (!mod || bookmarkBusy || readerState?.bookmarked) return;
    setBookmarkBusy(true);
    setBookmarkAck(null);
    setBookmarkErr(null);
    try {
      await apiJson('/api/account/bookmarks', {
        method: 'POST',
        body: JSON.stringify({ moduleId: mod.id }),
      });
      setReaderState((s) => (s ? { ...s, bookmarked: true } : s));
      setBookmarkAck('Saved to study.');
      apiJson('/api/interactions', {
        method: 'POST',
        body: JSON.stringify({ moduleId: mod.id, kind: 'bookmarked' }),
      }).catch(() => {});
    } catch {
      setBookmarkErr('Could not save. Check your connection and try again.');
    } finally {
      setBookmarkBusy(false);
    }
  };

  const saveNote = async () => {
    if (!mod || noteBusy) return;
    const body = noteBody.trim();
    if (!body) return;
    setNoteBusy(true);
    setNoteAck(null);
    setNoteErr(null);
    try {
      await apiJson('/api/account/notes', {
        method: 'POST',
        body: JSON.stringify({
          moduleId: mod.id,
          body,
          ...(noteMicroId ? { microModuleId: noteMicroId } : {}),
        }),
      });
      setReaderState((s) => (s ? { ...s, notePreview: body.slice(0, 280) } : s));
      setNoteAck('Saved note.');
      setNoteBody('');
      setNoteMicroId('');
      apiJson('/api/interactions', {
        method: 'POST',
        body: JSON.stringify({
          moduleId: mod.id,
          kind: 'note_saved',
          payload: noteMicroId ? { microModuleId: noteMicroId } : undefined,
        }),
      }).catch(() => {});
    } catch {
      setNoteErr('Could not save note. Check your connection and try again.');
    } finally {
      setNoteBusy(false);
    }
  };

  const sendInteraction = useCallback(
    async (kind: 'agreed' | 'confused' | 'completed') => {
      if (!mod) return;
      setFeedbackStatus('saving');
      setFeedbackAck(null);
      const optimistic = (prev: ReaderState | null): ReaderState | null =>
        prev
          ? {
              ...prev,
              helpfulFlag: kind === 'agreed' ? true : prev.helpfulFlag,
              confusingFlag: kind === 'confused' ? true : prev.confusingFlag,
              completionPercent: kind === 'completed' ? 100 : prev.completionPercent,
              progressState: kind === 'completed' ? 'completed' : prev.progressState,
            }
          : prev;
      try {
        await apiJson('/api/interactions', {
          method: 'POST',
          body: JSON.stringify({ moduleId: mod.id, kind }),
        });
        setReaderState(optimistic);
        try {
          if (kind === 'completed') {
            await apiJson('/api/events', {
              method: 'POST',
              body: JSON.stringify({
                eventType: 'module_completed',
                entityType: 'module',
                entityId: mod.id,
              }),
            });
          }
        } catch {
          /* completion analytics optional */
        }
        setFeedbackStatus('saved');
        const labels: Record<'agreed' | 'confused' | 'completed', string> = {
          agreed: 'Thanks - marked as helpful.',
          confused: 'Thanks - we noted this felt confusing.',
          completed: 'Marked complete. You can keep revisiting anytime.',
        };
        setFeedbackAck(labels[kind]);
      } catch {
        try {
          const key = `wi_feedback_${mod.slug}`;
          localStorage.setItem(key, kind);
          setReaderState(optimistic);
          setFeedbackStatus('saved');
          setFeedbackAck('Saved on this device.');
        } catch {
          setFeedbackStatus('error');
          setFeedbackAck('Could not save - check your connection and try again.');
        }
      }
    },
    [mod]
  );

  const chapterItems = useMemo(() => {
    if (!mod?.blocks?.length) return [];
    // Chapters should be *structure*, not every block.
    // Only include explicit heading/subheading blocks as anchors.
    const headingTypes = new Set(['heading', 'subheading']);
    return mod.blocks
      .filter((b) => headingTypes.has(String(b.blockType)))
      .map((b, i) => ({
        id: `block-${b.id}`,
        label: blockNavLabel(b, i),
      }));
  }, [mod]);

  const perspectiveOptions = useMemo(() => {
    const fromCards = (mod?.perspectives ?? []).map((p) => p.perspective_key).filter(Boolean);
    const fromBlocks = (mod?.blocks ?? []).flatMap((b) => (Array.isArray(b.perspectives) ? b.perspectives : []));
    const merged = [...new Set([...fromCards, ...fromBlocks].map((x) => String(x).trim().toLowerCase()).filter(Boolean))];
    return merged;
  }, [mod?.perspectives, mod?.blocks]);

  const { nextStepRels, otherRels } = useMemo(() => {
    const rels = mod?.relationships ?? [];
    const nextStepRels = rels.filter((r) => /follow|next|adjacent|prerequisite/i.test(r.kind));
    const otherRels = rels.filter((r) => !/follow|next|adjacent|prerequisite/i.test(r.kind));
    return { nextStepRels, otherRels };
  }, [mod?.relationships]);

  const deeperThoughtRels = useMemo(() => {
    const rels = mod?.relationships ?? [];
    // Prefer strict canonical kind; keep legacy fallback so older content still works.
    return rels.filter((r) => r.kind === 'deeper_thought' || /deeper|thought|nuance|submodule|lane/i.test(r.kind));
  }, [mod?.relationships]);

  const visibleSections = useMemo(() => {
    if (!mod?.sections?.length) return [];
    return mod.sections.filter((s) => layerVisible(s.layer, mode));
  }, [mod, mode]);

  const backHref = journeyCtx
    ? `/journeys/${journeyCtx.slug}`
    : from === 'compare'
      ? '/compare/islam-christianity-jesus'
      : from === 'deeper' && parentSlug
        ? `/m/${parentSlug}`
        : '/explore';

  // Auto-select the reader's lens when available, but never hide core (core blocks have no tags).
  useEffect(() => {
    const desired = profileLens ?? audienceTag;
    if (!desired) return;
    if (activePerspective !== 'all') return;
    // Always select the desired lens; block filtering is tag-based and safe even if a module
    // has no matching perspective blocks (it will simply show core blocks).
    setActivePerspective(desired);
  }, [profileLens, audienceTag, perspectiveOptions, activePerspective]);

  return (
    <Shell>
      <div ref={scrollRef} />
      {!mod && !error ? (
        <div className="wi-module-loading">
          <p className="muted">Loading…</p>
        </div>
      ) : null}
      {error ? <p className="wi-module-error">{error}</p> : null}
      {mod ? (
        <BooksReaderModuleShell
          title={mod.title}
          estimatedMinutes={mod.estimated_read_minutes}
          scrollPct={scrollPct}
          backHref={backHref}
          bookmarked={!!readerState?.bookmarked}
          bookmarkBusy={bookmarkBusy}
          bookmarkAck={bookmarkAck}
          bookmarkErr={bookmarkErr}
          onSaveBookmark={() => void saveBookmark()}
          toc={
            <>
              {mod.framing ? (
                <a className="books-reader-toc-item" href="#framing-block">
                  Why it matters
                </a>
              ) : null}
              {visibleSections.map((s) => (
                <a key={s.id} className="books-reader-toc-item" href={`#section-${s.id}`}>
                  {s.title ?? 'Section'}
                </a>
              ))}
              {mod.perspectives?.length ? (
                <a className="books-reader-toc-item" href="#perspectives">
                  Perspectives
                </a>
              ) : null}
              {chapterItems.map((item) => (
                <a key={item.id} className="books-reader-toc-item" href={`#${item.id}`}>
                  {item.label}
                </a>
              ))}
              {nextStepRels.length ? (
                <a className="books-reader-toc-item" href="#next-steps">
                  Often next
                </a>
              ) : null}
              <a className="books-reader-toc-item" href="#related">
                Related
              </a>
            </>
          }
          notePanel={
            <>
              {mod.microModules?.length ? (
                <label className="wi-module-note-anchor">
                  <span className="wi-module-note-anchor-label">Anchor to section (optional)</span>
                  <select
                    className="wi-module-note-select"
                    value={noteMicroId}
                    onChange={(e) => setNoteMicroId(e.target.value)}
                    aria-label="Attach note to micro-section"
                  >
                    <option value="">Whole module</option>
                    {mod.microModules.map((mm) => (
                      <option key={mm.id} value={mm.id}>
                        {mm.position}. {mm.title ?? 'Section'}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <textarea
                className="wi-module-note"
                rows={5}
                placeholder="A takeaway, question, or phrase to revisit…"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
              />
              <button type="button" className="wi-module-note-save" disabled={noteBusy} onClick={saveNote}>
                {noteBusy ? 'Saving...' : 'Save note'}
              </button>
              {noteAck ? <p className="muted small-print">{noteAck}</p> : null}
              {noteErr ? <p className="muted small-print">{noteErr}</p> : null}
              <p className="wi-module-aside-note">Notes are private to your account when signed in.</p>
            </>
          }
          morePanel={
            <>
              {deeperThoughtRels.length ? (
                <>
                  <p className="wi-module-panel__title">Deeper thoughts</p>
                  <div className="wi-module-panel__block">
                    <p className="wi-module-panel__sub">Sub-lanes inside this module</p>
                    <ul className="wi-module-toc wi-module-toc--panel">
                      {deeperThoughtRels.map((r) => (
                        <li key={r.id}>
                          <Link to={`/m/${r.slug}?from=deeper&parent=${encodeURIComponent(mod.slug)}`}>{r.title}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : null}

              <p className="wi-module-panel__title">Journeys &amp; continue</p>
              {mod.journeysContaining?.length ? (
                <div className="wi-module-panel__block">
                  <p className="wi-module-panel__sub">In journeys</p>
                  <ul className="wi-module-toc wi-module-toc--panel">
                    {mod.journeysContaining.map((j) => (
                      <li key={j.slug}>
                        <Link to={`/journeys/${j.slug}`}>
                          {j.title}
                          <span className="wi-module-journey-step"> (step {j.step_position})</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {mod.topics?.length ? (
                <div className="wi-module-panel__block">
                  <p className="wi-module-panel__sub">Theme links</p>
                  <ul className="wi-module-toc wi-module-toc--panel">
                    {mod.topics.map((topic) => (
                      <li key={topic.slug}>
                        <Link to="/browse">
                          {topic.title}
                          {topic.family ? <span className="wi-module-journey-step"> ({topic.family})</span> : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {mod.comparePages?.length ? (
                <div className="wi-module-panel__block">
                  <p className="wi-module-panel__sub">Compare from here</p>
                  <ul className="wi-module-toc wi-module-toc--panel">
                    {mod.comparePages.map((page) => (
                      <li key={page.slug}>
                        <Link to={`/compare/${page.slug}`}>{page.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="wi-module-panel__block">
                <p className="wi-module-panel__sub">Topics</p>
                <Link to="/explore" className="wi-module-aside-link">
                  Big Ideas
                </Link>
                <Link to="/browse" className="wi-module-aside-link">
                  Browse the library
                </Link>
                <Link to="/journeys" className="wi-module-aside-link">
                  Browse journeys
                </Link>
                <Link
                  to={
                    mod.comparePages?.[0]?.slug
                      ? `/compare/${mod.comparePages[0].slug}`
                      : '/compare/islam-christianity-jesus'
                  }
                  className="wi-module-aside-link"
                >
                  {mod.comparePages?.[0]?.title ?? 'Compare: Jesus in Islam & Christianity'}
                </Link>
              </div>
            </>
          }
          contentExtraClass={mode === 'study' ? 'wi-module-books-study-depth' : undefined}
        >
              <nav className="wi-module-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span aria-hidden> / </span>
                {journeyCtx ? (
                  <>
                    <Link to="/journeys">Journeys</Link>
                    <span aria-hidden> / </span>
                    <Link to={`/journeys/${journeyCtx.slug}`}>{journeyCtx.title}</Link>
                    <span aria-hidden> / </span>
                  </>
                ) : from === 'compare' ? (
                  <>
                    <Link to="/compare/islam-christianity-jesus">Compare</Link>
                    <span aria-hidden> / </span>
                  </>
                ) : from === 'deeper' && parentSlug ? (
                  <>
                    <Link to={`/m/${parentSlug}`}>Module</Link>
                    <span aria-hidden> / </span>
                    <span className="muted">Deeper thought</span>
                    <span aria-hidden> / </span>
                  </>
                ) : (
                  <>
                    <Link to="/explore">Big Ideas</Link>
                    <span aria-hidden> / </span>
                  </>
                )}
                <span className="wi-module-breadcrumb__here">{mod.title}</span>
              </nav>

              {journeyCtx ? (
                <div className="wi-module-journey-banner" role="status">
                  <span className="wi-module-journey-banner__label">Journey</span>
                  <span className="wi-module-journey-banner__text">
                    <Link to={`/journeys/${journeyCtx.slug}`}>{journeyCtx.title}</Link>
                    <span className="wi-module-journey-banner__step">
                      {' '}
                      · Step {journeyCtx.step} of {journeyCtx.total}
                    </span>
                  </span>
                </div>
              ) : null}

              <header className="wi-module-hero">
                <p className="wi-module-eyebrow">Module</p>
                <h1 className="wi-module-title">{mod.title}</h1>
                {mod.summary ? <p className="wi-module-lede">{formatInline(mod.summary)}</p> : null}
                <div className="wi-module-chips" aria-label="Module metadata">
                  {audienceTag ? (
                    <span className="wi-module-chip wi-module-chip--topic">For {audienceTag.replace(/_/g, ' ')}</span>
                  ) : null}
                  {humanizeTopicFamily(mod.topic_family) ? (
                    <span className="wi-module-chip wi-module-chip--topic">{humanizeTopicFamily(mod.topic_family)}</span>
                  ) : null}
                  {humanizeDifficulty(mod.difficulty) ? (
                    <span className="wi-module-chip wi-module-chip--level">{humanizeDifficulty(mod.difficulty)}</span>
                  ) : null}
                  {mod.estimated_read_minutes != null ? (
                    <span className="wi-module-chip wi-module-chip--time">~{mod.estimated_read_minutes} min read</span>
                  ) : null}
                </div>
                <div className="wi-module-meta">
                  {mod.review_state ? (
                    <span className="wi-module-review">Editorial status: {mod.review_state}</span>
                  ) : null}
                </div>
              </header>

              <div className="wi-module-depth" role="group" aria-label="Reading depth">
                <span className="wi-module-depth__label">Depth</span>
                <div className="wi-module-depth__buttons">
                  {depthModes.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={mode === d.id ? 'wi-module-depth__btn wi-module-depth__btn--active' : 'wi-module-depth__btn'}
                      onClick={() => setDepth(d.id)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {perspectiveOptions.length && !profileLens && !hasOnboarding ? (
                <div className="wi-module-perspective-filter" role="group" aria-label="Perspective">
                  <span className="wi-module-depth__label">Perspective</span>
                  <div className="wi-module-depth__buttons">
                    <button
                      type="button"
                      className={
                        activePerspective === 'all'
                          ? 'wi-module-depth__btn wi-module-depth__btn--active'
                          : 'wi-module-depth__btn'
                      }
                      onClick={() => setActivePerspective('all')}
                    >
                      All
                    </button>
                    {perspectiveOptions.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={
                          activePerspective === p ? 'wi-module-depth__btn wi-module-depth__btn--active' : 'wi-module-depth__btn'
                        }
                        onClick={() => setActivePerspective(p)}
                      >
                        {p.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {mod.framing ? (
                <section className="wi-module-block wi-module-framing" id="framing-block">
                  <h2 className="wi-module-h2">Why this question matters</h2>
                  {mod.framing.issue ? <p>{mod.framing.issue}</p> : null}
                  {mod.framing.why_asked ? <p className="muted">{mod.framing.why_asked}</p> : null}
                  {mod.framing.why_matters ? <p>{mod.framing.why_matters}</p> : null}
                </section>
              ) : null}

              {visibleSections.map((s) => (
                <section key={s.id} className="wi-module-block wi-module-layer-section" id={`section-${s.id}`}>
                  {s.title ? <h2 className="wi-module-h2">{s.title}</h2> : null}
                  {s.body_md ? <MdSection body={s.body_md} /> : null}
                </section>
              ))}

              {mod.perspectives?.length ? (
                <section className="wi-module-perspectives" id="perspectives">
                  <h2 className="wi-module-h2">Perspectives &amp; transparency</h2>
                  <p className="wi-module-perspectives-lede muted">
                    Different lenses on the same question—without flattening disagreement or hiding tension.
                  </p>
                  <div className="wi-module-perspective-grid">
                    {mod.perspectives
                      .filter((p) => activePerspective === 'all' || p.perspective_key.toLowerCase() === activePerspective)
                      .map((p) => (
                      <article key={p.id} className="wi-module-perspective-card">
                        <p className="wi-module-perspective-key">{p.perspective_key}</p>
                        {p.title ? <h3>{p.title}</h3> : null}
                        {p.body_md ? <MdSection body={p.body_md} /> : null}
                      </article>
                      ))}
                  </div>
                </section>
              ) : null}

              {mod.blocks?.map((b) => (
                <div key={b.id} id={`block-${b.id}`} className="wi-module-block-wrap">
                  <BlockRenderer block={b} mode={mode} audienceTag={audienceTag} activePerspective={activePerspective} />
                </div>
              ))}

              <div className="wi-module-endcap">
                {nextStepRels.length ? (
                  <section className="wi-module-block wi-module-related wi-module-related--next" id="next-steps">
                    <h2 className="wi-module-h2">People often explore next</h2>
                    <ul className="wi-module-related-list">
                      {nextStepRels.map((r) => (
                        <li key={r.id}>
                          <Link className="wi-module-related-card" to={`/m/${r.slug}`}>
                            <span className="wi-module-related-kind">{r.kind}</span>
                            <span className="wi-module-related-title">{r.title}</span>
                            <span className="wi-module-related-arrow" aria-hidden>
                              →
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <section className="wi-module-block wi-module-related" id="related">
                  <h2 className="wi-module-h2">Related questions &amp; graph</h2>
                  {otherRels.length ? (
                    <ul className="wi-module-related-list">
                      {otherRels.map((r) => (
                        <li key={r.id}>
                          <Link className="wi-module-related-card" to={`/m/${r.slug}`}>
                            <span className="wi-module-related-kind">{r.kind}</span>
                            <span className="wi-module-related-title">{r.title}</span>
                            <span className="wi-module-related-arrow" aria-hidden>
                              →
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : !nextStepRels.length ? (
                    <p className="wi-module-related-empty muted">More links will appear as the editorial graph grows.</p>
                  ) : (
                    <p className="wi-module-related-empty muted">Additional related modules appear here as the graph expands.</p>
                  )}
                </section>

                <section className="wi-module-feedback" aria-label="Your response">
                  <h2 className="wi-module-h2">Was this helpful?</h2>
                  <p className="wi-module-feedback-lede">Your response helps us improve these modules.</p>
                  <div className="wi-module-feedback-row">
                    <button
                      type="button"
                      className={`wi-module-feedback-chip${readerState?.helpfulFlag ? ' wi-module-feedback-chip--active' : ''}`}
                      disabled={feedbackStatus === 'saving'}
                      onClick={() => sendInteraction('agreed')}
                    >
                      {readerState?.helpfulFlag ? 'OK ' : ''}Helpful
                    </button>
                    <button
                      type="button"
                      className={`wi-module-feedback-chip wi-module-feedback-chip--muted${
                        readerState?.confusingFlag ? ' wi-module-feedback-chip--active' : ''
                      }`}
                      disabled={feedbackStatus === 'saving'}
                      onClick={() => sendInteraction('confused')}
                    >
                      {readerState?.confusingFlag ? 'OK ' : ''}Confusing
                    </button>
                    <button
                      type="button"
                      className={`wi-module-feedback-chip${
                        readerState?.progressState === 'completed' || (readerState?.completionPercent ?? 0) >= 100
                          ? ' wi-module-feedback-chip--active'
                          : ''
                      }`}
                      disabled={feedbackStatus === 'saving'}
                      onClick={() => sendInteraction('completed')}
                    >
                      {readerState?.progressState === 'completed' || (readerState?.completionPercent ?? 0) >= 100 ? 'OK ' : ''}
                      Mark complete
                    </button>
                  </div>
                  {feedbackAck ? (
                    <p className="wi-module-feedback-ack" role="status" aria-live="polite">
                      {feedbackAck}
                    </p>
                  ) : null}
                  {feedbackStatus === 'saving' ? (
                    <p className="wi-module-feedback-ack wi-module-feedback-ack--pending" aria-live="polite">
                      Saving…
                    </p>
                  ) : null}
                </section>
              </div>
        </BooksReaderModuleShell>
      ) : null}
    </Shell>
  );
}
